import { useEffect, useMemo, useRef, useState } from 'react';

declare global {
  interface Window {
    truste?: {
      eu?: {
        clickListener?: () => void;
      };
    };
  }
}

/**
 * TrustArc consent model: category 0 = Required (strictly necessary); higher
 * ids are the configurable buckets (the GSMA domain currently exposes 0–4).
 * Category 0 is treated as necessary and excluded from the
 * "any non-required" check by default.
 */
const DEFAULT_REQUIRED_CATEGORY_IDS = [0];

export interface UseTrustArcConsentOptions {
  autoRefresh?: boolean;
  /** Category ids treated as necessary/required (defaults to `[0]`). */
  requiredCategoryIds?: number[];
  pollIntervalMs?: number;
  onChange?: (state: TrustArcConsentState) => void;
}

export interface TrustArcConsentState {
  ready: boolean;

  hasInteracted: boolean;

  raw: {
    notice_gdpr_prefs?: string;
    cmapi_cookie_privacy?: string;
  };

  /** Consented TrustArc category ids (0 = Required). */
  consentedCategories: number[];

  /** True once the user has consented to any non-required category. */
  acceptedAnyNonNecessary: boolean;

  isCategoryEnabled: (categoryId: number) => boolean;

  /** Opens the TrustArc preference manager. */
  openCookieSettings: () => void;
}

/**
 * Parse `document.cookie` into an object.
 */
function readCookies(): Record<string, string> {
  if (typeof document === 'undefined') {
    return {};
  }
  return document.cookie
    .split(';')
    .reduce<Record<string, string>>((acc, part) => {
      const [rawKey, ...rest] = part.trim().split('=');
      const key = decodeURIComponent(rawKey || '');
      const value = decodeURIComponent(rest.join('=') || '');
      if (key) {
        acc[key] = value;
      }
      return acc;
    }, {});
}

/**
 * Parse the consented category ids out of TrustArc's `notice_gdpr_prefs`
 * cookie. Format is a comma-separated list of permitted category ids,
 * optionally followed by `:<hash>` — e.g. `0,1,2:abc123`.
 */
function parseConsentedCategories(noticeGdprPrefs?: string): number[] {
  if (!noticeGdprPrefs) {
    return [];
  }
  const list = noticeGdprPrefs.split(':')[0];
  if (!list) {
    return [];
  }
  return list
    .split(',')
    .map(value => Number(value.trim()))
    .filter(value => Number.isInteger(value));
}

function sameNumbers(a: number[], b: number[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Read and react to TrustArc cookie consent state. Polls the consent cookies
 * (TrustArc reloads the page on submit, so this also picks up implied consent)
 * and exposes whether any non-required category has been granted — the gate
 * used to load analytics for GSMA.
 */
export function useTrustArcConsent(
  options: UseTrustArcConsentOptions = {
    autoRefresh: true,
  },
): TrustArcConsentState {
  const {
    requiredCategoryIds = DEFAULT_REQUIRED_CATEGORY_IDS,
    pollIntervalMs = 1000,
    onChange,
  } = options;

  const [rawCookies, setRawCookies] = useState<{
    notice_gdpr_prefs?: string;
    cmapi_cookie_privacy?: string;
  }>({});
  const [consentedCategories, setConsentedCategories] = useState<number[]>([]);
  const readyRef = useRef(false);
  const lastEmittedRef = useRef<string>('');

  // Read cookies & parse consented categories.
  const refresh = () => {
    if (typeof document === 'undefined') {
      return;
    }

    const cookies = readCookies();
    const notice_gdpr_prefs = cookies['notice_gdpr_prefs'];
    const cmapi_cookie_privacy = cookies['cmapi_cookie_privacy'];

    const newCategories = parseConsentedCategories(notice_gdpr_prefs);

    // Functional updates: the interval captures the first render's `refresh`,
    // so comparing against closured state would see stale values and push a
    // fresh object every poll. Returning `prev` keeps the reference stable and
    // lets React bail out of the re-render.
    setRawCookies(prev =>
      prev.notice_gdpr_prefs === notice_gdpr_prefs &&
      prev.cmapi_cookie_privacy === cmapi_cookie_privacy
        ? prev
        : { notice_gdpr_prefs, cmapi_cookie_privacy },
    );
    setConsentedCategories(prev =>
      sameNumbers(prev, newCategories) ? prev : newCategories,
    );
    if (!readyRef.current && (notice_gdpr_prefs || cmapi_cookie_privacy)) {
      // Ready after the first meaningful read on the client.
      readyRef.current = true;
    }
  };

  useEffect(() => {
    // Always read once on mount so one-shot consumers (autoRefresh: false)
    // still get the current consent state.
    refresh();

    if (!options.autoRefresh) {
      return;
    }

    const id = window.setInterval(refresh, pollIntervalMs);
    const onVisibility = () =>
      document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollIntervalMs, options.autoRefresh]);

  const state: TrustArcConsentState = useMemo(() => {
    const hasInteracted = Boolean(
      rawCookies.notice_gdpr_prefs || rawCookies.cmapi_cookie_privacy,
    );

    const required = new Set(requiredCategoryIds);
    const acceptedAnyNonNecessary = consentedCategories.some(
      id => !required.has(id),
    );

    function openCookieSettings() {
      if (typeof window !== 'undefined' && window.truste?.eu?.clickListener) {
        window.truste.eu.clickListener();
      }
    }

    return {
      ready: readyRef.current || typeof window !== 'undefined',
      hasInteracted,
      raw: rawCookies,
      consentedCategories,
      acceptedAnyNonNecessary,
      isCategoryEnabled: (categoryId: number) =>
        consentedCategories.includes(categoryId),
      openCookieSettings,
    };
  }, [consentedCategories, requiredCategoryIds, rawCookies]);

  // Fire onChange only when the serialized snapshot changes.
  useEffect(() => {
    if (!onChange) {
      return;
    }
    const snapshot = JSON.stringify(state);
    if (snapshot !== lastEmittedRef.current) {
      lastEmittedRef.current = snapshot;
      onChange(state);
    }
  }, [state, onChange]);

  return state;
}
